import { useCallback, useEffect, useMemo, useState } from "react";

import { Asn1HexPanel } from "./components/Asn1HexPanel";
import { CaAdminPage } from "./components/CaAdminPage";
import { CaPage } from "./components/CaPage";
import { CertChainPanel } from "./components/CertChainPanel";
import { EnrollPage } from "./components/EnrollPage";
import { DropZone } from "./components/DropZone";
import { ExportBar } from "./components/ExportBar";
import { TokenSummary } from "./components/TokenSummary";
import { TrustStorePanel } from "./components/TrustStorePanel";
import { VerdictBanner } from "./components/VerdictBanner";
import { useHashWorker } from "./hooks/useHashWorker";
import { parseTsr, type ParsedTsr } from "./lib/parse";
import {
  certSha256Fingerprint,
  verifyChain,
  verifyDocumentDigest,
  verifySignature,
  webCryptoAlgoForImprint,
  type VerificationResult,
} from "./lib/verify";
import {
  addUserRoot,
  bundledRoots,
  loadUserRoots,
  removeUserRoot,
  type TrustRoot,
} from "./trust/store";

export interface LoadedTsr {
  fileName: string;
  bytes: Uint8Array;
  parsed: ParsedTsr;
}

export interface LoadedDoc {
  fileName: string;
  size: number;
  file: File;
}

function useHashRoute(): string {
  const [route, setRoute] = useState(() => window.location.hash);
  useEffect(() => {
    const onChange = () => setRoute(window.location.hash);
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}

export default function App() {
  const route = useHashRoute();
  const [tsr, setTsr] = useState<LoadedTsr | null>(null);
  const [doc, setDoc] = useState<LoadedDoc | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [userRoots, setUserRoots] = useState<TrustRoot[]>(() => loadUserRoots());
  const [verification, setVerification] = useState<VerificationResult | null>(
    null
  );
  const [fingerprints, setFingerprints] = useState<string[]>([]);
  const { digestHex, progress, hashFile, resetHash } = useHashWorker();

  const bundled = useMemo(() => bundledRoots(), []);
  const allRoots = useMemo(
    () => [...bundled, ...userRoots],
    [bundled, userRoots]
  );

  const acceptFiles = useCallback(
    async (files: File[]) => {
      setParseError(null);
      for (const file of files) {
        const bytes = new Uint8Array(await file.arrayBuffer());
        let parsed: ParsedTsr | null = null;
        if (bytes[0] === 0x30 && file.size < 1024 * 1024) {
          try {
            parsed = parseTsr(bytes);
          } catch (err) {
            if (/\.(tsr|tst|p7s|der)$/i.test(file.name)) {
              setParseError(err instanceof Error ? err.message : String(err));
              continue;
            }
            // not a token — fall through and treat as the original document
          }
        }
        if (parsed) {
          setTsr({ fileName: file.name, bytes, parsed });
          resetHash();
        } else {
          setDoc({ fileName: file.name, size: file.size, file });
        }
      }
    },
    [resetHash]
  );

  // Hash the original document with the token's imprint algorithm.
  useEffect(() => {
    if (!tsr || !doc) return;
    const algo = webCryptoAlgoForImprint(tsr.parsed);
    if (algo) hashFile(doc.file, algo);
  }, [tsr, doc, hashFile]);

  // Run the three verification checks.
  useEffect(() => {
    if (!tsr) {
      setVerification(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const [signature, chain, document] = await Promise.all([
        verifySignature(tsr.parsed),
        verifyChain(
          tsr.parsed,
          allRoots.map((r) => r.cert)
        ),
        verifyDocumentDigest(tsr.parsed, doc && digestHex ? digestHex : null),
      ]);
      if (!cancelled) setVerification({ signature, chain, document });
    })();
    return () => {
      cancelled = true;
    };
  }, [tsr, doc, digestHex, allRoots]);

  // Cert SHA-256 fingerprints are async (WebCrypto).
  useEffect(() => {
    if (!tsr?.parsed.signedData) {
      setFingerprints([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const certs = (tsr.parsed.signedData!.certificates ?? []).filter(
        (c): c is import("pkijs").Certificate => "subjectPublicKeyInfo" in c
      );
      const fps = await Promise.all(certs.map(certSha256Fingerprint));
      if (!cancelled) setFingerprints(fps);
    })();
    return () => {
      cancelled = true;
    };
  }, [tsr]);

  const loadExample = useCallback(async () => {
    const [tsrResp, docResp] = await Promise.all([
      fetch("/example/example.tsr"),
      fetch("/example/example-doc.txt"),
    ]);
    const tsrBytes = new Uint8Array(await tsrResp.arrayBuffer());
    const docBlob = await docResp.blob();
    const docFile = new File([docBlob], "example-doc.txt");
    await acceptFiles([
      new File([tsrBytes as unknown as BlobPart], "example.tsr"),
      docFile,
    ]);
  }, [acceptFiles]);

  const reset = useCallback(() => {
    setTsr(null);
    setDoc(null);
    setParseError(null);
    setVerification(null);
    resetHash();
  }, [resetHash]);

  return (
    <div className="app">
      <header className="app-header">
        <div>
          <p className="eyebrow">Sunlit Bytes · Timestamp Authority</p>
          <h1>TSR Viewer</h1>
          <p className="lede">
            Inspect and verify RFC&nbsp;3161 timestamp tokens entirely in your
            browser. Nothing you drop here leaves your machine.
          </p>
        </div>
        <nav className="app-nav">
          <a href={route.startsWith("#/ca") ? "#/" : "#/ca"}>
            {route.startsWith("#/ca") ? "Viewer" : "CA"}
          </a>
          {route.startsWith("#/ca") && <a href="#/ca/admin">Admin</a>}
          <a href="/policy">Policy</a>
          <a href="/roots.pem">Root cert</a>
        </nav>
      </header>

      {route.startsWith("#/enroll") ? (
        <EnrollPage />
      ) : route === "#/ca/admin" ? (
        <CaAdminPage />
      ) : route === "#/ca" ? (
        <CaPage />
      ) : (
        <>
      <DropZone
        tsrName={tsr?.fileName ?? null}
        docName={doc?.fileName ?? null}
        parseError={parseError}
        hashProgress={progress}
        onFiles={acceptFiles}
        onLoadExample={loadExample}
        onReset={reset}
      />

      {tsr && (
        <>
          {verification && (
            <VerdictBanner
              parsed={tsr.parsed}
              verification={verification}
              docName={doc?.fileName ?? null}
            />
          )}
          <TokenSummary parsed={tsr.parsed} fileName={tsr.fileName} />
          <div className="panels">
            <CertChainPanel
              certs={tsr.parsed.certs}
              signerIndex={tsr.parsed.signerCertIndex}
              fingerprints={fingerprints}
              genTime={tsr.parsed.tstInfo?.genTime ?? null}
            />
            <Asn1HexPanel bytes={tsr.bytes} />
          </div>
          <ExportBar
            fileName={tsr.fileName}
            parsed={tsr.parsed}
            verification={verification}
          />
        </>
      )}

      <TrustStorePanel
        bundled={bundled}
        userRoots={userRoots}
        onAdd={(pem) => {
          addUserRoot(pem);
          setUserRoots(loadUserRoots());
        }}
        onRemove={(subject) => {
          removeUserRoot(subject);
          setUserRoots(loadUserRoots());
        }}
      />
        </>
      )}

      <footer className="app-footer">
        <p>
          A timestamp token proves a TSA attested to a hash at a time. It does
          not prove a document's contents are true.
        </p>
        <p>
          <a href="https://www.rfc-editor.org/rfc/rfc3161">RFC 3161</a> ·{" "}
          <a href="/policy">Sunlit Bytes TSA policy</a>
        </p>
      </footer>
    </div>
  );
}
